if (!customElements.get('cart-drawer')) {
  customElements.define(
    'cart-drawer',
    class CartDrawer extends HTMLElement {
      constructor() {
        super();

        this.overlay = this.querySelector('[data-cart-drawer-overlay]');
        this.panel = this.querySelector('.cart-drawer__panel');
        this.body = this.querySelector('[data-cart-drawer-body]');
        this.footer = this.querySelector('[data-cart-drawer-footer]');

        this.overlay.addEventListener('click', this.close.bind(this));
        this.querySelector('[data-cart-drawer-close]').addEventListener('click', this.close.bind(this));
        this.addEventListener('keydown', (evt) => {
          if (evt.key === 'Escape') this.close();
        });
        this.body.addEventListener('click', this.onBodyClick.bind(this));
        this.sectionUrl = `${window.location.pathname}?section_id=cart-drawer`;

        document.querySelectorAll('[data-cart-drawer-open]').forEach((trigger) => {
          trigger.addEventListener('click', (evt) => {
            evt.preventDefault();
            this.open();
          });
        });

        this.freeGiftSyncing = false;
      }

      connectedCallback() {
        // The drawer body is server-rendered on page load, so a gift added on a previous
        // visit (or left over after the cart emptied) needs a check even before any
        // add/remove interaction happens.
        this.checkFreeGifts();
      }

      open() {
        this.activeElement = document.activeElement;
        this.classList.add('is-open');
        this.scrollY = window.scrollY;
        document.documentElement.classList.add('cart-drawer-open');
        document.body.classList.add('cart-drawer-open');
        document.body.style.top = `-${this.scrollY}px`;
        this.panel.focus();
      }

      close() {
        this.classList.remove('is-open');
        document.documentElement.classList.remove('cart-drawer-open');
        document.body.classList.remove('cart-drawer-open');
        document.body.style.top = '';
        window.scrollTo(0, this.scrollY || 0);
        this.activeElement?.focus();
      }

      setActiveElement(el) {
        this.activeElement = el;
      }

      getSectionsToRender() {
        return [{ id: 'cart-drawer' }];
      }

      setLoading(isLoading) {
        this.classList.toggle('is-loading', isLoading);
      }

      // Paints the add immediately instead of waiting on the /cart/add.js round
      // trip. Checks the drawer's own current data first: if this variant is
      // already a real line item, bumps its visible qty in place (so re-adding the
      // same product doesn't flash a second, duplicate item); otherwise builds a
      // placeholder line from data already known on the page (see data-product-*
      // on <product-form>). Once the real response comes back, applySectionHtml()
      // replaces the whole body innerHTML with server truth anyway, so both cases
      // are naturally corrected then - no separate cleanup needed on success, only
      // on error (see removeOptimisticItem).
      addOptimisticItem({ variantId, title, image, price, priceCents, quantity }) {
        this.open();
        this.classList.remove('is-empty');
        if (this.footer) this.footer.hidden = false;

        const existing = variantId
          ? this.body.querySelector(`[data-cart-drawer-item][data-variant-id="${variantId}"]`)
          : null;

        if (existing) {
          const qtyEl = existing.querySelector('[data-qty-value]');
          if (qtyEl) qtyEl.textContent = (Number(qtyEl.textContent) || 0) + quantity;
          // The displayed qty is now ahead of what the server still thinks this
          // line is, so block +/- until the response settles and repaints this
          // node with the real (button-wired) markup - otherwise a click here
          // would send a stale quantity.
          existing.querySelectorAll('button').forEach((btn) => (btn.disabled = true));
        } else {
          this.body.classList.remove('cart-drawer__body--empty');
          this.body.querySelector('[data-cart-drawer-empty]')?.remove();

          let list = this.body.querySelector('[data-cart-drawer-items]');
          if (!list) {
            list = document.createElement('ul');
            list.className = 'cart-drawer__items';
            list.setAttribute('data-cart-drawer-items', '');
            this.body.appendChild(list);
          }

          const li = document.createElement('li');
          li.className = 'cart-drawer__item is-optimistic';
          li.setAttribute('data-cart-drawer-optimistic', '');

          const imageWrap = document.createElement('span');
          imageWrap.className = 'cart-drawer__item-image';
          if (image) {
            const img = document.createElement('img');
            img.src = image;
            img.alt = '';
            img.loading = 'lazy';
            img.width = 80;
            imageWrap.appendChild(img);
          }

          const details = document.createElement('div');
          details.className = 'cart-drawer__item-details';

          const titleEl = document.createElement('span');
          titleEl.className = 'cart-drawer__item-title';
          titleEl.textContent = title;

          const priceWrap = document.createElement('div');
          priceWrap.className = 'cart-drawer__item-price';
          if (price) {
            const priceEl = document.createElement('span');
            priceEl.className = 'cart-drawer__item-price-final';
            priceEl.textContent = price;
            priceWrap.appendChild(priceEl);
          }

          const footerRow = document.createElement('div');
          footerRow.className = 'cart-drawer__item-footer';

          const qtyWrap = document.createElement('div');
          qtyWrap.className = 'cart-drawer__item-quantity';
          // No data-line/data-cart-drawer-item on this node, so the drawer's click
          // delegation (onBodyClick) never matches these - they're inert until the
          // real response repaints this line with working buttons.
          const decreaseBtn = document.createElement('button');
          decreaseBtn.type = 'button';
          decreaseBtn.className = 'cart-drawer__qty-button';
          decreaseBtn.disabled = true;
          decreaseBtn.setAttribute('aria-label', 'Decrease quantity');
          decreaseBtn.textContent = '−';
          const qtyValue = document.createElement('span');
          qtyValue.className = 'cart-drawer__qty-value';
          qtyValue.textContent = quantity;
          const increaseBtn = document.createElement('button');
          increaseBtn.type = 'button';
          increaseBtn.className = 'cart-drawer__qty-button';
          increaseBtn.disabled = true;
          increaseBtn.setAttribute('aria-label', 'Increase quantity');
          increaseBtn.textContent = '+';
          qtyWrap.append(decreaseBtn, qtyValue, increaseBtn);

          const removeBtn = document.createElement('button');
          removeBtn.type = 'button';
          removeBtn.className = 'cart-drawer__item-remove';
          removeBtn.disabled = true;
          removeBtn.textContent = 'Entfernen';

          footerRow.append(qtyWrap, removeBtn);
          details.append(titleEl, priceWrap, footerRow);
          li.append(imageWrap, details);
          list.prepend(li);
        }

        document.querySelectorAll('[data-cart-count]').forEach((el) => {
          el.textContent = (Number(el.textContent) || 0) + quantity;
          el.classList.remove('bubble-hidden');
        });

        this.optimisticQuantity = (this.optimisticQuantity || 0) + quantity;

        // Fake-activate the free-gift progress tiers against the projected total
        // right away, instead of waiting for the real response to repaint them. The
        // widget itself is always rendered (see cart-drawer.liquid), just hidden
        // when the real total didn't warrant it yet - so on a first add to an empty
        // cart this reveals it instead of needing to build it from scratch in JS.
        if (priceCents) {
          const projectedTotal = (Number(this.dataset.cartTotal) || 0) + priceCents * quantity;
          this.dataset.cartTotal = projectedTotal;

          const addOnWrapper = this.querySelector('[data-cart-add-on-wrapper]');
          if (addOnWrapper && projectedTotal > 0) addOnWrapper.hidden = false;

          this.updateAddOnProgress(projectedTotal);
        }
      }

      // Rollback path used only when the add request actually failed: strips the
      // placeholder line item back out and re-fetches the real cart state, since at
      // that point we no longer know what the count/footer looked like before the
      // optimistic guess was painted in.
      removeOptimisticItem() {
        const nodes = this.body.querySelectorAll('[data-cart-drawer-optimistic]');
        if (!nodes.length) return;
        nodes.forEach((el) => el.remove());

        const removedQty = this.optimisticQuantity || 0;
        this.optimisticQuantity = 0;

        document.querySelectorAll('[data-cart-count]').forEach((el) => {
          const next = Math.max(0, (Number(el.textContent) || 0) - removedQty);
          el.textContent = next;
          el.classList.toggle('bubble-hidden', next === 0);
        });

        this.refresh();
      }

      // Opens instantly and paints from the sections HTML that /cart/add.js already
      // returns (requested via getSectionsToRender), instead of waiting on the two
      // extra round trips refresh()+renderSection() used to require.
      renderContents(response) {
        this.open();

        const sectionHtml = response && response.sections && response.sections['cart-drawer'];
        if (!sectionHtml) {
          this.refresh().finally(() => this.setLoading(false));
          return;
        }

        this.applySectionHtml(sectionHtml);
        this.setLoading(false);

        if (!this.getFreeGiftConfigs().length) return;

        // Free-gift rules need full cart data (variant id, price) that isn't present
        // in the section HTML, so reconcile them separately - fully in the background.
        // Gift items are already excluded from the visible item list (see
        // cart-drawer.liquid), so there's nothing worth a full drawer re-render for;
        // syncFreeGiftsSilently only touches the count badge and progress widget.
        fetch(`${window.routes.cart_url}.js`)
          .then((response) => response.json())
          .then((cart) => this.syncFreeGiftsSilently(cart))
          .catch((e) => console.error(e));
      }

      applySectionHtml(html) {
        const doc = new DOMParser().parseFromString(html, 'text/html');

        const newDrawer = doc.querySelector('cart-drawer');
        this.classList.toggle('is-empty', !!newDrawer && newDrawer.classList.contains('is-empty'));
        if (newDrawer) this.dataset.cartTotal = newDrawer.dataset.cartTotal || '0';

        const newBody = doc.querySelector('[data-cart-drawer-body]');
        if (newBody) {
          this.body.className = newBody.className;
          this.body.innerHTML = newBody.innerHTML;
        }

        const newFooter = doc.querySelector('[data-cart-drawer-footer]');
        if (newFooter) {
          this.footer.innerHTML = newFooter.innerHTML;
          this.footer.hidden = newFooter.hidden;
        }

        const itemCount = Array.from(this.body.querySelectorAll('[data-qty-value]')).reduce(
          (sum, el) => sum + (Number(el.textContent) || 0),
          0
        );
        document.querySelectorAll('[data-cart-count]').forEach((el) => {
          el.textContent = itemCount;
          el.classList.toggle('bubble-hidden', itemCount === 0);
        });
      }

      onBodyClick(evt) {
        const upsellButton = evt.target.closest('.upsell-card-to-cart');
        if (upsellButton) {
          this.addUpsellItem(upsellButton);
          return;
        }

        const item = evt.target.closest('[data-cart-drawer-item]');
        if (!item) return;
        const line = Number(item.dataset.line);

        if (evt.target.closest('[data-cart-drawer-remove]')) {
          this.updateItem(line, 0, item);
        } else if (evt.target.closest('[data-qty-increase]')) {
          const value = Number(item.querySelector('[data-qty-value]').textContent);
          this.updateItem(line, value + 1, item);
        } else if (evt.target.closest('[data-qty-decrease]')) {
          const value = Number(item.querySelector('[data-qty-value]').textContent);
          this.updateItem(line, value - 1, item);
        }
      }

      addUpsellItem(button) {
        if (button.disabled || button.classList.contains('adding')) return;
        const variantId = button.dataset.variantId;
        button.classList.add('adding');

        const title = button.dataset.productTitle;
        if (title) {
          this.addOptimisticItem({
            variantId,
            title,
            image: button.dataset.productImage || '',
            price: button.dataset.productPrice || '',
            priceCents: Number(button.dataset.productPriceCents) || 0,
            quantity: 1,
          });
        }

        // Request the section HTML in the same call (like product-form.js does)
        // instead of a separate refresh() round trip afterwards.
        fetch(`${window.routes.cart_add_url}.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            id: variantId,
            quantity: 1,
            sections: this.getSectionsToRender().map((section) => section.id),
            sections_url: window.location.pathname,
          }),
        })
          .then((response) => response.json())
          .then((response) => {
            if (response.status) {
              this.removeOptimisticItem();
              console.error(response.description || response.message);
              return;
            }
            this.renderContents(response);
          })
          .catch((e) => {
            console.error(e);
            this.removeOptimisticItem();
          })
          .finally(() => button.classList.remove('adding'));
      }

      updateItem(line, quantity, itemEl) {
        // Guard against a second click firing while this item's request is still in
        // flight (rapid double-click, or a duplicate click/touch event on mobile).
        // Line numbers are positional, so an overlapping second request can land
        // after the first has already shifted the array and silently miss its
        // target - the item then looks stuck until a genuinely new click retries it.
        if (itemEl && itemEl.classList.contains('is-loading')) return;

        if (itemEl) {
          itemEl.classList.add('is-loading');
          itemEl.querySelectorAll('button').forEach((btn) => (btn.disabled = true));
        }

        fetch(`${window.routes.cart_change_url}.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ line, quantity }),
        })
          .then((response) => response.json())
          .then((cart) => this.renderCart(cart))
          .catch((e) => console.error(e))
          .finally(() => {
            if (!itemEl) return;
            itemEl.classList.remove('is-loading');
            itemEl.querySelectorAll('button').forEach((btn) => (btn.disabled = false));
          });
      }

      refresh() {
        return fetch(`${window.routes.cart_url}.js`)
          .then((response) => response.json())
          .then((cart) => this.renderCart(cart));
      }

      renderCart(cart) {
        // A gift-only cart (last real item just removed) should read as empty -
        // matches the real_item_count logic in cart-drawer.liquid.
        const itemCount = this.realItemCount(cart);
        this.classList.toggle('is-empty', itemCount === 0);
        this.dataset.cartTotal = cart.total_price;

        document.querySelectorAll('[data-cart-count]').forEach((el) => {
          el.textContent = itemCount;
          el.classList.toggle('bubble-hidden', itemCount === 0);
        });

        return this.syncFreeGifts(cart).then((synced) => {
          if (synced) return;
          return this.renderSection();
        });
      }

      getGiftVariantIds() {
        return this.getFreeGiftConfigs().map((config) => Number(config.variantId));
      }

      // Cart item count excluding gift line items, so a cart holding only a
      // free gift still counts (and renders) as empty.
      realItemCount(cart) {
        const giftVariantIds = this.getGiftVariantIds();
        return cart.items
          .filter((item) => !giftVariantIds.includes(item.variant_id))
          .reduce((sum, item) => sum + item.quantity, 0);
      }

      // Reads the free-gift rules published in layout/theme.liquid (Shopify.cart_drawer.freeProductN)
      // and adds/removes the matching variant so the drawer always reflects the current cart total.
      getFreeGiftConfigs() {
        const drawerData = window.Shopify && window.Shopify.cart_drawer;
        if (!drawerData) return [];

        return [drawerData.freeProduct1, drawerData.freeProduct2].filter(
          (config) => config && config.variantId && config.available === 'true'
        );
      }

      // Lightweight check used on connect: fetches the live cart and syncs gifts
      // silently (no full section re-render needed - see syncFreeGiftsSilently).
      checkFreeGifts() {
        if (!this.getFreeGiftConfigs().length) return;

        fetch(`${window.routes.cart_url}.js`)
          .then((response) => response.json())
          .then((cart) => this.syncFreeGiftsSilently(cart))
          .catch((e) => console.error(e));
      }

      // Compares the live cart against the configured free-gift rules and returns
      // the { variantId: quantity } updates payload needed to reconcile them, or
      // {} if the cart already matches what the rules expect.
      computeGiftUpdates(cart) {
        const configs = this.getFreeGiftConfigs();
        if (!configs.length) return {};

        // Exclude the gifts themselves from the qualifying total - otherwise a rule of 0
        // (or a gift that's already in the cart) would always "qualify" on its own price.
        const giftVariantIds = this.getGiftVariantIds();
        const nonGiftItems = cart.items.filter((item) => !giftVariantIds.includes(item.variant_id));
        const nonGiftTotal = nonGiftItems.reduce((sum, item) => sum + item.final_line_price, 0) / 100;
        const hasNonGiftItems = nonGiftItems.length > 0;

        const updates = {};

        configs.forEach((config) => {
          if (config.condition !== 'cart-total') return;

          const variantId = Number(config.variantId);
          const rule = Number(config.rule) || 0;
          const shouldHaveGift = hasNonGiftItems && nonGiftTotal >= rule;
          const hasGift = cart.items.some((item) => item.variant_id === variantId);

          if (shouldHaveGift && !hasGift) {
            updates[variantId] = 1;
          } else if (!shouldHaveGift && hasGift) {
            updates[variantId] = 0;
          }
        });

        return updates;
      }

      // Used by flows that already need a full drawer re-render anyway (quantity
      // changes, refresh()): reconciles gifts and, once settled, always ends in a
      // renderSection() so the real item-list change gets painted too. Returns a
      // promise resolving to true once any triggered update has actually finished.
      syncFreeGifts(cart) {
        if (this.freeGiftSyncing) return Promise.resolve(false);

        const updates = this.computeGiftUpdates(cart);
        if (Object.keys(updates).length === 0) return Promise.resolve(false);

        this.freeGiftSyncing = true;
        return fetch(`${window.routes.cart_update_url}.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ updates }),
        })
          .then((response) => response.json())
          .then((updatedCart) => this.renderCart(updatedCart).then(() => true))
          .catch((e) => {
            console.error(e);
            return false;
          })
          .finally(() => {
            this.freeGiftSyncing = false;
          });
      }

      // Used right after add-to-cart and on connect: the drawer body already
      // excludes gift line items from its item list, so when there's nothing to
      // reconcile this skips re-fetching HTML entirely and only patches the count
      // badge and progress widget. But when a gift line actually gets added or
      // removed, every real item's data-line (see cart-drawer.liquid's
      // forloop.index) is a *positional* index into cart.items - shifting the
      // gift's presence can shift those positions for any real item after it, even
      // though none of them visibly moved. So once an update is actually applied,
      // this always does the full render, to keep those line numbers accurate for
      // the next remove/qty click (a stale line silently targets the wrong item).
      syncFreeGiftsSilently(cart) {
        if (this.freeGiftSyncing) return Promise.resolve(false);

        const updates = this.computeGiftUpdates(cart);
        if (Object.keys(updates).length === 0) {
          this.applyLightCartUpdate(cart);
          return Promise.resolve(false);
        }

        this.freeGiftSyncing = true;
        return fetch(`${window.routes.cart_update_url}.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ updates }),
        })
          .then((response) => response.json())
          .then((updatedCart) =>
            this.renderSection().then(() => {
              this.applyLightCartUpdate(updatedCart);
              return true;
            })
          )
          .catch((e) => {
            console.error(e);
            return false;
          })
          .finally(() => {
            this.freeGiftSyncing = false;
          });
      }

      // Silent counterpart to applySectionHtml: updates only the count badge and
      // the free-gift progress widget, without touching the item list markup.
      applyLightCartUpdate(cart) {
        const itemCount = this.realItemCount(cart);
        this.classList.toggle('is-empty', itemCount === 0);
        this.dataset.cartTotal = cart.total_price;

        document.querySelectorAll('[data-cart-count]').forEach((el) => {
          el.textContent = itemCount;
          el.classList.toggle('bubble-hidden', itemCount === 0);
        });

        this.updateAddOnProgress(cart.total_price);
      }

      // Recomputes each free-gift progress tile's active/comming state from the
      // rule thresholds baked into the markup (see cart-free-add-on.liquid),
      // mirroring that template's active/elsif-comming logic without a fetch.
      updateAddOnProgress(totalPriceCents) {
        const total = totalPriceCents / 100;

        this.querySelectorAll('[data-add-on-rule]').forEach((el) => {
          const rule = Number(el.dataset.addOnRule) || 0;
          const isActive = total >= rule;
          el.classList.toggle('active', isActive);

          if (isActive) {
            el.classList.remove('comming');
            return;
          }

          const prevRule = el.dataset.addOnPrevRule;
          const isComming = prevRule === undefined ? true : total >= Number(prevRule);
          el.classList.toggle('comming', isComming);
        });
      }

      renderSection() {
        return fetch(this.sectionUrl)
          .then((response) => response.text())
          .then((html) => {
            const doc = new DOMParser().parseFromString(html, 'text/html');

            const newDrawer = doc.querySelector('cart-drawer');
            if (newDrawer) this.dataset.cartTotal = newDrawer.dataset.cartTotal || '0';

            const newBody = doc.querySelector('[data-cart-drawer-body]');
            if (newBody) {
              this.body.className = newBody.className;
              this.body.innerHTML = newBody.innerHTML;
            }

            const newFooter = doc.querySelector('[data-cart-drawer-footer]');
            if (newFooter) {
              this.footer.innerHTML = newFooter.innerHTML;
              this.footer.hidden = newFooter.hidden;
            }
          })
          .catch((e) => console.error(e));
      }
    }
  );
}
